import React, { useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { getStatusChipStyles } from '../../../utils/jobStatuses';

const defaultCenter = { lat: 39.8283, lng: -98.5795 };

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
};

function pinColorForStatus(statusId) {
  const styles = getStatusChipStyles(statusId);
  return styles?.borderColor || '#64748b';
}

export default function ControlCenterMap({
  jobs = [],
  selectedJobId,
  onSelectJob,
  height = 360,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mapRef = useRef(null);
  const [scriptReady, setScriptReady] = React.useState(
    Boolean(typeof window !== 'undefined' && window.google?.maps),
  );

  useEffect(() => {
    if (scriptReady) return undefined;
    const interval = setInterval(() => {
      if (window.google?.maps) {
        setScriptReady(true);
        clearInterval(interval);
      }
    }, 100);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [scriptReady]);

  const markers = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.latitude != null &&
          job.longitude != null &&
          !Number.isNaN(Number(job.latitude)) &&
          !Number.isNaN(Number(job.longitude)),
      ),
    [jobs],
  );

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps || markers.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((job) => {
      bounds.extend({ lat: Number(job.latitude), lng: Number(job.longitude) });
    });
    mapRef.current.fitBounds(bounds, 48);
  }, [markers]);

  if (!scriptReady) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (markers.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          borderLeft: { md: '1px solid' },
          borderColor: isDark ? '#334155' : '#e7e7e7',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No jobs with map locations
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, width: '100%', minHeight: 220 }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={defaultCenter}
        zoom={4}
        options={defaultOptions}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      >
        {markers.map((job) => (
          <Marker
            key={job.id}
            position={{ lat: Number(job.latitude), lng: Number(job.longitude) }}
            onClick={() => onSelectJob?.(job)}
            icon={
              window.google?.maps
                ? {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: selectedJobId === job.id ? 10 : 7,
                    fillColor: pinColorForStatus(job.status),
                    fillOpacity: 1,
                    strokeColor: selectedJobId === job.id ? '#ffffff' : pinColorForStatus(job.status),
                    strokeWeight: selectedJobId === job.id ? 2 : 1,
                  }
                : undefined
            }
          />
        ))}
      </GoogleMap>
    </Box>
  );
}
